import { useState, useEffect, useCallback, useRef } from 'react';
import { getPoranaytteetV2, createPoranayteV2, updatePoranayteV2, deletePoranayteV2, getTestiTyypit, getTestitV2, deleteTestiV2, getRakenneosaV2, updateRakenneosaV2 } from '../../../../../api/api';
import useApiFeedback from './useApiFeedback';
import { parseCaptionForEditor, serializeCaptionForSave } from './captionUtils';

// Service layer for PoranayteView — all data/API calls, no JSX.
export default function usePoranayteV2(rakId) {
  const [samples, setSamples] = useState([]);
  const [testiTyypit, setTestiTyypit] = useState([]);
  const [rakenneosa, setRakenneosa] = useState(null);
  const [loading, setLoading] = useState(false);
  const { feedback, run, clearFeedback } = useApiFeedback();

  useEffect(() => { getTestiTyypit().then(r => setTestiTyypit(r.data)).catch(() => {}); }, []);

  // loading only tracks the rakId-triggered load (this effect), not the
  // per-edit refreshes patch/addSample/removeSample also trigger below —
  // otherwise every keystroke-commit would flicker the table's spinner.
  useEffect(() => { if (rakId) setLoading(true); }, [rakId]);

  // Clear synchronously on every rakId change (not just when it goes
  // falsy, same as refreshRak's fix in useRakenneosaNavV2) — refresh()
  // below is reused for per-edit refreshes too, so it can't blank samples
  // itself without flickering the table on every field save. Without this,
  // switching rakenneosa left the PREVIOUS one's samples (and their typed
  // values) on screen — still fully editable — until the new fetch
  // resolved, so an edit made during that gap silently patched the wrong
  // rakenneosa's sample row.
  useEffect(() => { setSamples([]); }, [rakId]);

  // Always holds the CURRENT rakId, read synchronously during render (not
  // in an effect, which would lag a render behind) — see refresh() below.
  const latestRakIdRef = useRef(rakId);
  latestRakIdRef.current = rakId;

  // Guards against an out-of-order response: refresh() is reused by
  // patch/addSample/removeSample too, and each of those closes over
  // whatever `refresh` (and therefore `rakId`) was current when the field
  // was blurred. Typing a value and immediately switching rakenneosa fires
  // the blur's patch() with a STALE closure — its own refresh() re-fetches
  // the OLD rakId. Two things have to hold at once:
  //  (a) a stale response must never apply at all once its rakId is no
  //      longer current (`latestRakIdRef` check below), and
  //  (b) a stale call must never be able to invalidate the CURRENT
  //      rakId's own in-flight fetch. (a) alone isn't enough for (b): if
  //      the sequence counter were shared across all rakIds, the stale
  //      call still bumps it when invoked (even though its own response
  //      gets discarded by the rakId check), which falsely makes the
  //      current rakId's legitimate, still-in-flight response look
  //      "superseded" and get dropped too — leaving `samples` stuck at []
  //      (from the clear-on-switch effect above) with nothing to replace
  //      it. Fix: scope the sequence counter PER rakId (a plain object
  //      keyed by rakId, not a single ref) so a stale call for an old
  //      rakId can only ever race against other calls for that SAME old
  //      rakId, never against the current one's.
  const seqByRakIdRef = useRef({});
  const refresh = useCallback(() => {
    const requestRakId = rakId;
    if (!requestRakId) { setSamples([]); return; }
    const seq = (seqByRakIdRef.current[requestRakId] || 0) + 1;
    seqByRakIdRef.current[requestRakId] = seq;
    const isLatestForThisRakId = () => seq === seqByRakIdRef.current[requestRakId] && requestRakId === latestRakIdRef.current;
    getPoranaytteetV2(requestRakId)
      .then(r => {
        if (!isLatestForThisRakId()) return;
        setSamples((r.data || []).filter(s => s.id_rakenneosa === requestRakId));
      })
      .catch(() => {})
      .finally(() => { if (isLatestForThisRakId()) setLoading(false); });
  }, [rakId]);
  useEffect(refresh, [refresh]);

  // Poränäyte's own caption — one per rakenneosa, same shape as
  // Peitepaksuus's captions. Fetched separately from `samples` since it
  // lives on the rakenneosa row itself, not on any poränäyte row.
  useEffect(() => {
    if (!rakId) { setRakenneosa(null); return; }
    getRakenneosaV2(rakId).then(r => setRakenneosa(r.data)).catch(() => {});
  }, [rakId]);

  const caption = parseCaptionForEditor(rakenneosa?.poranayte_caption);
  // TaulukkoFooter's onChange fires on every keystroke (Lexical's
  // OnChangePlugin fires per edit, not on blur) — debounce the actual save
  // (same 600ms pattern Lisähuomioita below uses), and merge the saved
  // value into `rakenneosa` directly on success rather than refetching —
  // the PATCH response already confirms what was saved.
  const captionTimer = useRef(null);
  const commitCaption = useCallback((capData) => {
    if (!rakId) return;
    const targetRakId = rakId; // guards against switching rakenneosa mid-debounce
    const serialized = serializeCaptionForSave(capData);
    clearTimeout(captionTimer.current);
    captionTimer.current = setTimeout(async () => {
      try {
        await run(() => updateRakenneosaV2(targetRakId, { poranayte_caption: serialized }));
        setRakenneosa(prev => prev && prev.id === targetRakId ? { ...prev, poranayte_caption: serialized } : prev);
      } catch { /* captured in feedback */ }
    }, 600);
  }, [rakId, run]);

  const patch = useCallback(async (id, field, value) => {
    try { await run(() => updatePoranayteV2(id, { [field]: value })); }
    catch { /* captured in feedback */ }
    finally { refresh(); }
  }, [run, refresh]);

  const toggleTesti = useCallback(async (sample, opt, checked) => {
    const cur = new Set((sample.testi || '').split(',').map(s => s.trim()).filter(Boolean));
    if (checked) cur.add(opt); else cur.delete(opt);
    await patch(sample.id, 'testi', [...cur].join(', '));
    if (!checked) {
      // Only Poränäyte manages testi-row lifecycle: unchecking a type here
      // is the only way those rows get created (useTestiTypeV2 auto-creates
      // on eligibility) or removed — the testi-type blocks themselves have
      // no create/delete affordance of their own anymore.
      try {
        const existing = (await getTestitV2(sample.id)).data.filter(t => t.tyyppi === opt);
        await Promise.all(existing.map(t => deleteTestiV2(t.id)));
      } catch { /* best-effort cleanup, not user-blocking */ }
    }
  }, [patch]);

  // No auto-generated tunnus (was `PN${n}`) — that got written into the
  // real nayte_tunnus field and showed up IN the editable input itself,
  // not just as a label, so it looked like a real chosen name rather than
  // an empty one waiting to be filled in. Left empty (nullable, no
  // uniqueness check when empty — see backend julkisivuService.createPoranayte),
  // the card/table title falls back to a position label ("Näyte 1") for
  // display only, without writing anything that looks like real data.
  // Returns the created sample (or null on failure) so a caller — the
  // "+ Näyte" tabs view in particular — can select it immediately instead
  // of guessing which sample is "new" from the refreshed list's order.
  const addSample = useCallback(async (nayteTunnus) => {
    const tunnus = (nayteTunnus ?? '').trim();
    try {
      const res = await run(() => createPoranayteV2(rakId, { nayte_tunnus: tunnus || null }));
      return res.data;
    } catch { return null; }
    finally { refresh(); }
  }, [rakId, run, refresh]);

  const removeSample = useCallback(async (id) => {
    try { await run(() => deletePoranayteV2(id), 'Julkisivu-projektin päivitys onnistui — näyte poistettu'); }
    catch { /* captured in feedback */ }
    finally { refresh(); }
  }, [run, refresh]);

  return { samples, testiTyypit, loading, feedback, clearFeedback, patch, toggleTesti, addSample, removeSample, caption, commitCaption, rakenneosaId: rakenneosa?.id ?? null };
}
